import { Col } from "../UI/Layout/Col.tsx";
import { Row } from "../UI/Layout/Row.tsx";
import { InputWithLabel } from "../UI/InputWithLabel.tsx";
import { TextInput } from "../UI/TextInput.tsx";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { PrimaryButton } from "../UI/PrimaryButton.tsx";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useWithLoading } from "../UI/Loading/useWithLoading.ts";
import { PhoneInput } from "../UI/Phone/PhoneInput.tsx";
import { useAllUserGroups } from "../UserGroup/useAllUserGroups.ts";
import { MultipleSelect, Select } from "@normasteaching/norma-ui";
import { TeacherRole, TeacherTag } from "../gql/graphql.ts";
import {
  teacherRoleLabels,
  teacherTagLabels,
} from "./TeacherRoleAndTagLabels.ts";

export type FranchiseeFormInputs = {
  email: string;
  givenName: string;
  familyName: string;
  phoneNumber: {
    countryCode: string;
    number: string;
  };
  userGroupIds?: string[];
  lesson1on1?: boolean;
  internalTeacher?: boolean;
  roles: TeacherRole[];
  tags: TeacherTag[];
};

const roleOptions = Object.values(TeacherRole).map((role) => ({
  id: role,
  label: teacherRoleLabels[role],
}));
const tagOptions = Object.values(TeacherTag).map((tag) => ({
  id: tag,
  label: teacherTagLabels[tag],
}));

const schema = yup
  .object({
    email: yup
      .string()
      .email("Insert a valid email")
      .required("Insert an email"),
    givenName: yup.string().required("Insert a name"),
    familyName: yup.string().required("Insert a surname"),
    phoneNumber: yup.object({
      countryCode: yup.string().required("Select a mobile country code"),
      number: yup
        .string()
        .matches(/\d+/, {
          message: "Insert a valid phone number",
        })
        .required(),
    }),
    userGroupIds: yup.array(yup.string().required()).optional(),
    lesson1on1: yup.boolean().optional(),
    internalTeacher: yup.boolean().optional(),
    roles: yup
      .array(
        yup.mixed<TeacherRole>().oneOf(Object.values(TeacherRole)).required(),
      )
      .min(1, "Select at least one role")
      .required(),
    tags: yup
      .array(
        yup.mixed<TeacherTag>().oneOf(Object.values(TeacherTag)).required(),
      )
      .required(),
  })
  .required();

export function FranchiseeForm({
  onSave,
  disabledMap,
  defaultValues,
  showInternalTeacher = true,
}: {
  onSave: (data: FranchiseeFormInputs) => Promise<void>;
  disabledMap?: {
    [key in keyof FranchiseeFormInputs]?: boolean;
  };
  defaultValues?: FranchiseeFormInputs;
  showInternalTeacher?: boolean;
}) {
  const { withLoading, loading } = useWithLoading();
  const { userGroups } = useAllUserGroups();
  const userGroupOptions = (userGroups ?? []).map((ug) => ({
    id: ug.id,
    label: ug.name,
  }));
  const defaultUserGroupId =
    (defaultValues?.userGroupIds ?? ["adults"])?.[0] ?? "adults";
  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
  } = useForm<FranchiseeFormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      phoneNumber: {
        number: "",
        countryCode: "+39",
      },
      userGroupIds: ["adults"],
      internalTeacher: false,
      roles: [TeacherRole.Teacher],
      tags: [],
      ...defaultValues,
    },
  });

  const onSubmit: SubmitHandler<FranchiseeFormInputs> = async (data) => {
    await withLoading(async () => onSave(data));
  };

  return (
    <form>
      <Col>
        <Row>
          <InputWithLabel label="Name">
            <TextInput
              disabled={disabledMap?.givenName}
              errorMessage={errors.givenName?.message}
              {...register("givenName")}
            />
          </InputWithLabel>
          <InputWithLabel label="Surname">
            <TextInput
              disabled={disabledMap?.familyName}
              errorMessage={errors.familyName?.message}
              {...register("familyName")}
            />
          </InputWithLabel>
        </Row>
        <InputWithLabel label="Email">
          <TextInput
            disabled={disabledMap?.email}
            errorMessage={errors.email?.message}
            type={"email"}
            {...register("email")}
          />
        </InputWithLabel>
        <InputWithLabel label="Phone">
          <Controller
            control={control}
            render={({ field }) => {
              function getErrorMessage() {
                const parts: string[] = [];
                if (errors.phoneNumber?.number?.message)
                  parts.push(errors.phoneNumber?.number?.message);
                if (errors.phoneNumber?.countryCode?.message)
                  parts.push(errors.phoneNumber?.countryCode?.message);
                return parts.join(" | ");
              }
              return (
                <PhoneInput
                  errorMessage={getErrorMessage()}
                  value={field.value}
                  onChange={field.onChange}
                />
              );
            }}
            name={"phoneNumber"}
          />
        </InputWithLabel>
        <Row>
          <InputWithLabel label="User group">
            {disabledMap?.userGroupIds ? (
              <TextInput
                readOnly
                value={
                  userGroupOptions.find((o) => o.id === defaultUserGroupId)
                    ?.label ?? defaultUserGroupId
                }
              />
            ) : (
              <Controller
                control={control}
                name={"userGroupIds"}
                render={({ field }) => {
                  return (
                    <MultipleSelect<string>
                      options={userGroupOptions}
                      value={field.value ?? [defaultUserGroupId]}
                      onChange={field.onChange}
                    />
                  );
                }}
              />
            )}
          </InputWithLabel>
        </Row>
        <Row>
          <InputWithLabel label="1 on 1 Lesson">
            <Controller
              control={control}
              name={"lesson1on1"}
              render={({ field }) => {
                return (
                  <Select
                    disabled={disabledMap?.lesson1on1}
                    placeholder={"Select if 1 on 1"}
                    options={[
                      { name: "Yes", id: "true" },
                      { name: "No", id: "false" },
                    ]}
                    value={field.value ? "true" : "false"}
                    onChange={(val) => {
                      field.onChange(val === "true");
                    }}
                  />
                );
              }}
            />
          </InputWithLabel>
          {showInternalTeacher ? (
            <InputWithLabel label="Internal teacher">
              <Controller
                control={control}
                name={"internalTeacher"}
                render={({ field }) => {
                  return (
                    <Select
                      disabled={disabledMap?.internalTeacher}
                      placeholder={"Select if internal"}
                      options={[
                        { name: "No", id: "false" },
                        { name: "Yes", id: "true" },
                      ]}
                      value={field.value ? "true" : "false"}
                      onChange={(val) => {
                        field.onChange(val === "true");
                      }}
                    />
                  );
                }}
              />
            </InputWithLabel>
          ) : null}
        </Row>
        <Row>
          <InputWithLabel
            label="Roles"
            smallText={
              "Staff Norma members can only lead onboarding lives when chosen explicitly and are never assigned to lessons"
            }
          >
            <Controller
              control={control}
              name={"roles"}
              render={({ field, fieldState }) => (
                <MultipleSelect<string>
                  options={roleOptions}
                  value={field.value ?? []}
                  onChange={field.onChange}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />
          </InputWithLabel>
          <InputWithLabel label="Tags">
            <Controller
              control={control}
              name={"tags"}
              render={({ field }) => (
                <MultipleSelect<string>
                  options={tagOptions}
                  value={field.value ?? []}
                  onChange={field.onChange}
                  placeholder={"No tags"}
                />
              )}
            />
          </InputWithLabel>
        </Row>
        <div className={"flex justify-end"}>
          <PrimaryButton
            loading={loading}
            onClick={handleSubmit(onSubmit)}
            label={"Save"}
          />
        </div>
      </Col>
    </form>
  );
}
